import $ from 'jquery';
import _ from 'underscore';

import { FixedPositionLocation, PositioningMethod } from './position-options';

const offsetPositions = ['relative', 'absolute', 'fixed'];

function getDimensions(element) {
  const $element = $(element);

  return {
    width: $element.outerWidth(),
    height: $element.outerHeight(),
  };
}

function getBorderOffsets(element) {
  const borderLeft = parseFloat(element.css('border-left-width'));
  const borderTop = parseFloat(element.css('border-top-width'));

  return {
    leftBorderOffset: borderLeft,
    topBorderOffset: borderTop,
  };
}

function getOffsetWithBorderOffsets(element) {
  const { left, top } = element.offset();
  const {
    leftBorderOffset,
    topBorderOffset,
  } = getBorderOffsets(element);

  return {
    left: left + leftBorderOffset,
    top: top + topBorderOffset,
  };
}

function getContainerOffsetElement(container) {
  // if absolute position will be based off container, return container
  // if not, then return container's offset parent
  return _.contains(offsetPositions, container.css('position')) ?
    container : container.offsetParent();
}

function getParentOffset(container, anchor, anchorPosition) {
  const containerOffsetParent = getContainerOffsetElement(container);
  const anchorOffsetParent = anchor && anchor.offsetParent && anchor.offsetParent();

  // if anchorPosition was specified, it was meant for handling different parents manually,
  // so don't need to get an offset
  if (!_.isEmpty(anchorPosition) ||
    // if an offsetParent doesn't exist, we can't get the offset, so return 0s
    !containerOffsetParent || !containerOffsetParent.length ||
    !anchorOffsetParent || !anchorOffsetParent.length ||
    // if both are the same, we don't need the offset
    containerOffsetParent.get(0) === anchorOffsetParent.get(0)) {
    return { leftOffset: 0, topOffset: 0 };
  }

  const containerOffsetParentPosition = getOffsetWithBorderOffsets(containerOffsetParent);
  const anchorOffsetParentPosition = anchorOffsetParent.offset();

  return {
    leftOffset: containerOffsetParentPosition.left - anchorOffsetParentPosition.left,
    topOffset: containerOffsetParentPosition.top - anchorOffsetParentPosition.top,
  };
}

function getBoundingRect(element) {
  const position = element.position();
  const { width, height } = getDimensions(element);

  return {
    top: position.top,
    left: position.left,
    width,
    height,
  };
}

function testFit(containerDimensions, elementDimentions, position, { leftOffset, topOffset }) {
  const adjustedPosition = {
    top: position.top + topOffset,
    left: position.left + leftOffset,
  };

  // Calculating how much of the element intersects with the container
  const verticalFit = Math.max(
    0,
    Math.min(containerDimensions.height, adjustedPosition.top + elementDimentions.height) -
    Math.max(0, adjustedPosition.top)
  ) / elementDimentions.height;

  const horizontalFit = Math.max(
    0,
    Math.min(containerDimensions.width, adjustedPosition.left + elementDimentions.width) -
    Math.max(0, adjustedPosition.left)
  ) / elementDimentions.width;

  return {
    verticalFit,
    horizontalFit,
    get areaFit() {
      return verticalFit * horizontalFit;
    },
  };
}

function getFixedPosition(positionOptions, container, anchor, popupDimensions) {
  const { height: elementHeight, width: elementWidth } = popupDimensions;
  const { anchorPosition } = positionOptions || { };
  const {
    left, top, height, width,
  } = anchorPosition && !_.isEmpty(anchorPosition) ?
    anchorPosition : getBoundingRect(anchor);
  const { leftOffset, topOffset } = getParentOffset(container, anchor, anchorPosition);

  const offsetLeft = left - leftOffset;
  const offsetTop = top - topOffset;

  switch (positionOptions.location) {
    case FixedPositionLocation.bottomLeftCorner: return {
      top: offsetTop + height,
      left: offsetLeft - elementWidth,
    };
    case FixedPositionLocation.topRightCorner: return {
      top: offsetTop - elementHeight,
      left: offsetLeft + width,
    };
    case FixedPositionLocation.topLeftCorner: return {
      top: offsetTop - elementHeight,
      left: offsetLeft - elementWidth,
    };
    case FixedPositionLocation.bottomRightCorner: return {
      top: offsetTop + height,
      left: offsetLeft + width,
    };
    case FixedPositionLocation.bottomLeft: return {
      top: offsetTop + height,
      left: (offsetLeft + width) - elementWidth,
    };
    case FixedPositionLocation.topRight: return {
      top: offsetTop - elementHeight,
      left: offsetLeft,
    };
    case FixedPositionLocation.topLeft: return {
      top: offsetTop - elementHeight,
      left: (offsetLeft + width) - elementWidth,
    };
    case FixedPositionLocation.bottomRight:
    default: return {
      top: offsetTop + height,
      left: offsetLeft,
    };
  }
}

function getFittingPosition(positionOptions, container, anchor, popupDimensions) {
  const containerDimensions = getDimensions(container);
  const anchorPosition = positionOptions ? positionOptions.anchorPosition : null;

  const positions = [
    getFixedPosition(
      { location: FixedPositionLocation.bottomRight, anchorPosition },
      container, anchor, popupDimensions
    ),
    getFixedPosition(
      { location: FixedPositionLocation.bottomLeft, anchorPosition },
      container, anchor, popupDimensions
    ),
    getFixedPosition(
      { location: FixedPositionLocation.topRight, anchorPosition },
      container, anchor, popupDimensions
    ),
    getFixedPosition(
      { location: FixedPositionLocation.topLeft, anchorPosition },
      container, anchor, popupDimensions
    ),
  ];

  const threshold = positionOptions && positionOptions.threshold;

  const parentOffset = getParentOffset(container, anchor, anchorPosition);

  // try to find position with 100% fitting
  let fittingPosition = _.find(positions, position =>
    testFit(containerDimensions, popupDimensions, position, parentOffset).areaFit >= 1);

  // when no 100% fitting and has threshold try to find it with threshold fitting
  // threshold is 0, 1
  if (!fittingPosition) {
    fittingPosition = threshold &&
      (_.find(positions, position =>
        testFit(
          containerDimensions,
          popupDimensions,
          position,
          parentOffset
        ).areaFit >= threshold) || positions[0]);
  }

  // when no 100% fitting and no threshold or not threshold fitting
  // try to find maxmium fitting
  if (!fittingPosition) {
    fittingPosition = _.max(
      positions,
      position => testFit(containerDimensions, popupDimensions, position, parentOffset).areaFit
    );
  }

  return _.pick(fittingPosition, 'left', 'top');
}

function getPositioningMethod(positioningMethod) {
  switch (positioningMethod) {
    case PositioningMethod.fixed:
      return getFixedPosition;
    case PositioningMethod.fit:
    default:
      return getFittingPosition;
  }
}

export function getPopupPosition(
  positioningMethod,
  positioningOptions,
  container,
  anchor,
  popupContainer
) {
  const popupDimensions = getDimensions(popupContainer);
  const { height, width } = popupDimensions;

  // set width-heigh so the positioning isn't broken by the element resizing
  if (width && height) {
    popupContainer.css({
      width,
      height,
    });
  }

  // Hide element so it doesn't affect other dimensions
  popupContainer.hide();

  const position = getPositioningMethod(positioningMethod)(
    positioningOptions,
    container, anchor, popupDimensions
  );

  // Show element again
  popupContainer.show();

  return position;
}
